import { computed, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import usePrompts from '@/composables/usePrompts'
import { parseEntityResponse } from '@/shared/utils/workflowSpec'
import { SchemaType } from '@/constants/schema'

const useGenerateEntity = () => {
  const store = useStore()
  const { proxy } = getCurrentInstance()
  const workflow = computed(() => store.state.workflow.workflow)

  const { getSingleEntity } = usePrompts()

  const generateEntity = async ({ name, description }) => {
    let newEntities

    if (!workflow.value.aiDescription) {
      await store.dispatch('workflow/openWorkflowDescriptionDialog')
      return
    }

    proxy.$loadingState.start()

    try {
      const res = await getSingleEntity(name, description)

      if (!res?.entity) {
        store.commit('snackbar/showMessage', {
          content: 'The AI was unable to create an entity. Please try adjusting the description.',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        proxy.$loadingState.stop()
        return
      }

      const entityNameToIdMap = new Map()
      for (const schema of workflow.value.schemas || []) {
        if (schema.type === SchemaType.ENTITY) {
          entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
        }
      }

      const entityTable = parseEntityResponse(res.entity, entityNameToIdMap)
      const entityExists = (workflow.value.schemas ?? []).some((item) => item.name === entityTable.name)

      if (entityTable && !entityExists) {
        newEntities = [...safeStructuredClone(workflow.value.schemas ?? []), entityTable]
      }

      if (newEntities?.length > 0) {
        await workflowApi.updateSchemas({
          schemas: newEntities,
          workflow: workflow.value
        })
      }

      proxy.$loadingState.stop()

      return entityTable
    } catch (error) {
      if (error.name !== 'AbortError') {
        await store.dispatch('workflow/openRetryDialog', {
          failedItems: [{ type: 'entity', name: 'Generate Entity' }],
          retryCallback: () => generateEntity({ name, description })
        })
      }
      proxy.$loadingState.stop()
    }
  }

  return { generateEntity }
}

export default useGenerateEntity
